/**
 * FRONT-1346: stop a lone `~` from becoming strikethrough.
 *
 * Model-authored text is full of approximations — "~1h", "~€50", "~25%". GFM
 * strikethrough is `~~text~~`, but marked's `del` tokenizer also pairs SINGLE
 * tildes, so two approximations in one paragraph struck out everything between
 * them.
 *
 * Escaping the lone tildes before parsing is the fix rather than overriding
 * marked's tokenizer: a `tokenizer` override that returns falsy falls back to
 * marked's original, which would re-match the very thing we're rejecting.
 *
 * Code is skipped on purpose. Inside a fenced block or an inline code span a
 * backslash is not an escape, it is a visible character — turning `~/bin` in a
 * shell snippet into `\~/bin` would be a worse bug than the one being fixed.
 */

/** ``` or ~~~ fences, kept verbatim along with their contents. */
const FENCE_BLOCK = /(^|\n)[ \t]*(```|~~~)[\s\S]*?(?:\n[ \t]*\2[ \t]*(?=\n|$)|$)/g;
/** Inline code spans — any run of backticks, matched to the same length. */
const CODE_SPAN = /(`+)[\s\S]*?\1/g;

/** Escape runs of exactly one tilde; leave `~~` (real strikethrough) alone. */
function escapeInPlainText(text: string): string {
  return text.replace(/~+/g, (run) => (run.length === 1 ? "\\~" : run));
}

/**
 * Apply `transform` to everything except the regions matched by `pattern`,
 * which are copied through untouched.
 */
function mapOutside(
  source: string,
  pattern: RegExp,
  transform: (chunk: string) => string,
): string {
  let out = "";
  let last = 0;
  // `pattern` carries /g; reset so a shared literal can't leak state.
  pattern.lastIndex = 0;
  for (let m = pattern.exec(source); m !== null; m = pattern.exec(source)) {
    out += transform(source.slice(last, m.index)) + m[0];
    last = m.index + m[0].length;
  }
  return out + transform(source.slice(last));
}

/**
 * Escape lone tildes in `source`, leaving fenced blocks, inline code spans and
 * genuine `~~strikethrough~~` untouched.
 */
export function escapeLoneTildes(source: string): string {
  if (!source.includes("~")) return source;
  return mapOutside(source, FENCE_BLOCK, (outsideFences) =>
    mapOutside(outsideFences, CODE_SPAN, escapeInPlainText),
  );
}
